import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Category, CategoryCreate, CategoryUpdate } from '@/app/types/category';
import { Loader2 } from 'lucide-react';

const categoryFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  description: z.string().max(500, 'Description must be less than 500 characters').optional().nullable(),
  parent_id: z.number().nullable().optional(),
  is_active: z.boolean(),
  display_order: z.number().int().min(0).optional().nullable(),
  code: z.string().max(50, 'Code must be less than 50 characters').optional().nullable(),
});

type CategoryFormValues = z.infer<typeof categoryFormSchema>;

interface CategoryInfoFormProps {
  category?: Category;
  onSubmit: (data: CategoryCreate | CategoryUpdate) => void;
  isSubmitting: boolean;
  parentCategories: Category[];
}

export function CategoryInfoForm({
  category,
  onSubmit,
  isSubmitting,
  parentCategories,
}: CategoryInfoFormProps) {
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: category?.name || '',
      description: category?.description || '',
      parent_id: category?.parent_id || null,
      is_active: category?.is_active ?? true,
      display_order: category?.display_order || 0,
      code: category?.code || '',
    },
  });

  const handleSubmit = (values: CategoryFormValues) => {
    const data = {
      ...values,
      description: values.description || null,
      code: values.code || null,
      display_order: values.display_order || null,
    };
    
    if (category) {
      // For update, only send changed fields
      const updateData: CategoryUpdate = {};
      if (data.name !== category.name) updateData.name = data.name;
      if (data.description !== category.description) updateData.description = data.description;
      if (data.parent_id !== category.parent_id) updateData.parent_id = data.parent_id;
      if (data.is_active !== category.is_active) updateData.is_active = data.is_active;
      if (data.display_order !== category.display_order) updateData.display_order = data.display_order;
      if (data.code !== category.code) updateData.code = data.code;
      onSubmit(updateData);
    } else {
      onSubmit(data as CategoryCreate);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter category name" />
                </FormControl>
                <FormDescription>
                  The name of the category as it will appear in the system
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Code Field */}
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    value={field.value || ''} 
                    placeholder="Enter category code (optional)" 
                  />
                </FormControl>
                <FormDescription>
                  An optional unique code for the category
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Parent Category Field */}
          <FormField
            control={form.control}
            name="parent_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Parent Category</FormLabel>
                <Select
                  value={field.value?.toString() || "none"}
                  onValueChange={(value) => {
                    field.onChange(value === "none" ? null : parseInt(value, 10));
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No Parent (Top Level)</SelectItem>
                    {parentCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Select a parent category to create a hierarchy
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Display Order Field */}
          <FormField
            control={form.control}
            name="display_order"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Order</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    {...field} 
                    value={field.value || 0}
                    onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    placeholder="0" 
                  />
                </FormControl>
                <FormDescription>
                  Order in which the category appears (lower numbers appear first)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Description Field */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  value={field.value || ''} 
                  placeholder="Enter category description (optional)"
                  className="resize-none"
                  rows={4}
                />
              </FormControl>
              <FormDescription>
                Additional details about the category
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Active Status Field */}
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active Status</FormLabel>
                <FormDescription>
                  Inactive categories will not be available for selection
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {category ? 'Update Category' : 'Create Category'}
          </Button>
        </div>
      </form>
    </Form>
  );
}